import React, { useEffect, useMemo, useState } from 'react'
import ExposureBar from './ExposureBar'
import ActionButtonGroup from './ActionButtonGroup'
import StatusBadge from './StatusBadge'
import goalResultService from '../../../services/goalResultService'
import Pagination from '../../../components/Pagination'
import { MdDataSaverOff } from "react-icons/md";
import moment from 'moment';
import {
  DocumentChartBarIcon,
  PencilSquareIcon,
  WalletIcon,
  LockClosedIcon,
  KeyIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';

const tabs = [
  { id: 'Overview', label: 'Overview' },
  { id: 'Details', label: 'Vouchers' },
]

const voucherStatusStyles = {
  Settled: 'dark:bg-customFontLightGreen/20 bg-customFontLightGreen/10 text-customDarkGreen dark:text-customDarkGreen',
  Active: 'dark:bg-customFontYellow/30 bg-customFontYellow/20 text-customFontGold dark:text-customLightMilkWhite',
  Pending: 'dark:bg-custom_dark_background bg-custom_dark_secondary/10 text-custom_dark_secondary dark:text-customLightMilkWhite',
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

const SelectedEventCard = ({ event, isMobile, userDetails, setEventList }) => {
  const [activeTab, setActiveTab] = useState('Overview')
  const [expoTab,setExpoTab] = useState("body");
  const [voucherState, setVoucherState] = useState({
    loading: false,
    data: [],
    error: null,
  })
  const [voucherPage, setVoucherPage] = useState(1)
  const [voucherPageSize, setVoucherPageSize] = useState(5)
  const [voucherSearchQuery, setVoucherSearchQuery] = useState('')

  useEffect(() => {
    setActiveTab('Overview')
    setVoucherState({ loading: false, data: [], error: null })
    setVoucherPage(1)
  }, [event?.eventId])

  useEffect(() => {
    if (!event || activeTab !== 'Details') {
      return
    }

    setVoucherState({ loading: true, data: [], error: null })
    let isCancelled = false

    const timer = setTimeout(() => {
      fetchVouchersById(isCancelled);
    }, 250)

    return () => {
      isCancelled = true
      clearTimeout(timer)
    }
  }, [activeTab, event?.eventId])

  const fetchVouchersById = async (isCancelled) => {
    try {
      const payload = {
        rapidEventId: event?.eventId
      }

      const response = await goalResultService.getVouchers(payload);

      if (response.status === 200) {
        //const entry = detailsVoucherList.find(item => item.gamblingId === event.eventId)
        if (!isCancelled) {
          setVoucherState({
            loading: false,
            data: response.data.voucherData ?? [],
            error: null,
          })
        }
      }
      else {
          setVoucherState({
            loading: false,
            data: [],
            error: null,
          })
      }
    } catch (err) {
      if (!isCancelled) {
        setVoucherState({
          loading: false,
          data: [],
          error: 'Unable to load voucher details.',
        })
      }
    }
  }

  const { loading, data: vouchers, error } = voucherState
  const filteredVouchers = useMemo(() => {
    const query = voucherSearchQuery.trim().toLowerCase()
    if (!query) {
      return vouchers
    }

    return vouchers.filter(voucher => {
      const voucherId = (voucher.voucherNo ?? voucher.id ?? '').toString().toLowerCase()
      const username = (voucher.user ?? voucher.userName ?? '').toString().toLowerCase()
      const bettingType = (voucher.gamblingType ?? voucher.type ?? '').toString().toLowerCase()

      return (
        voucherId.includes(query) ||
        username.includes(query) ||
        bettingType.includes(query)
      )
    })
  }, [voucherSearchQuery, vouchers])
  const {
    safePage: voucherSafePage,
    totalPages: voucherTotalPages,
    pageItems: paginatedVouchers,
  } = useMemo(() => {
    const totalItems = filteredVouchers.length
    const clampedSize = Math.max(voucherPageSize, 1)
    const totalPages = Math.max(1, Math.ceil(totalItems / clampedSize))
    const safePage = Math.min(Math.max(voucherPage, 1), totalPages)
    const startIndex = totalItems === 0 ? 0 : (safePage - 1) * clampedSize
    const endIndex = totalItems === 0 ? 0 : Math.min(startIndex + clampedSize, totalItems)
    return {
      safePage,
      totalPages,
      pageItems: filteredVouchers.slice(startIndex, endIndex),
    }
  }, [filteredVouchers, voucherPage, voucherPageSize])

  useEffect(() => {
    if (voucherSafePage !== voucherPage) {
      setVoucherPage(voucherSafePage)
    }
  }, [voucherSafePage, voucherPage])

  useEffect(() => {
    setVoucherPage(1)
  }, [voucherSearchQuery])

  if (!event) {
    return (
      // <div className="rounded-xl bg-custom_dark_primary/60 p-6 ring-1 ring-white/5">
      //   <p className="text-white/70">No event selected</p>
      // </div>
       <div className="col-span-full flex items-center justify-center rounded-3xl border border-dashed border-customMuted dark:border-custom_dark_secondary bg-custom_dark_primary/50 dark:bg-custom_dark_primary2 py-20 text-center dark:text-customMuted text-customLightMilkWhite">
            No event selected.
        </div>
    )
  }

  const handleVoucherPageChange = (nextPage) => {
    const clampedPage = Math.min(Math.max(nextPage, 1), voucherTotalPages)
    setVoucherPage(clampedPage)
  }

  const handleVoucherPageSizeChange = (size) => {
    setVoucherPageSize(size)
    setVoucherPage(1)
  }

  const hasVouchers = !loading && !error && filteredVouchers.length > 0
  const noVoucherMessage = voucherSearchQuery.trim()
    ? 'No vouchers match your search.'
    : 'No voucher data available for this event.'

  return (
    <aside className="rounded-xl bg-customFontMilkWhite p-3 ring-1 ring-customBlack/10 shadow dark:bg-custom_dark_primary/60 dark:ring-custom_dark_secondary/50">
      <div className='flex justify-between items-center'>
        <div className="flex gap-2">
          {tabs.map(tab => {
            const isActive = tab.id === activeTab

            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`rounded-lg px-3 py-1 ${isMobile ? 'text-xs' : 'text-sm'} font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customBlue focus-visible:ring-offset-2 focus-visible:ring-offset-customFontMilkWhite dark:focus-visible:ring-offset-custom_dark_primary
                ${isActive
                    ? 'bg-customAccentBlue dark:bg-gradient-to-r from-customAccent via-customAccent/90 to-customAccent/80 text-white shadow hover:bg-customAccentBlue/90'
                    : 'border border-customBlack/10 bg-white/70 text-customBlack/70 hover:bg-white dark:border-white/10 dark:bg-white/5 dark:text-white/70 dark:hover:bg-white/10'
                  }`}
              >
                {tab.label}
              </button>
            )
          })}
        </div>
        <div className='flex flex-col justify-center items-center italic'>
        <span className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-customSecondaryText dark:text-customMuted`}>Total Single Stake</span>
        <span className='font-semibold text-sm text-background dark:text-customLightMilkWhite'>{event.totalBetting} Ks</span>
        </div>
      </div>
      {activeTab === 'Overview' && (
        <div className="mt-2 space-y-4">
          <section className="rounded-md bg-white/80 p-2 shadow-lg dark:bg-white/5 dark:ring-custom_dark_secondary/50 border border-customBlack/10 dark:border-custom_dark_secondary">
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <span className="text-xs uppercase tracking-wide text-customBlack/60 dark:text-white/50">{event.leagueName}</span>
                  <p className="text-lg font-semibold text-customBlack dark:text-white">{event.homeTeam} vs {event.awayTeam}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                    <svg className="h-4 w-4 text-blue/70 dark:text-white/60" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm0 3a1 1 0 011 1v5h4a1 1 0 110 2h-5a1 1 0 01-1-1V6a1 1 0 011-1z" />
                    </svg>
                    <span className="text-sm text-customBlack/70 dark:text-white/60">{moment(event.startTime).format("YYYY-MM-DD hh:mm A")}</span>
                    <StatusBadge value={event.status} />
                    <StatusBadge value={event.isFivePercentCom ? "Five" : "Eight"} />
                    {
                      !event.isSingle ?
                      <StatusBadge value={"Freezed"} type={"Single"}/> : null
                    }
                     {
                      !event.isMix ?
                      <StatusBadge value={"Freezed"} type={"Parlay"}/> : null
                    }
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <p className="text-xs font-bold text-customBlack/70 dark:text-white/70 italic">Myanmar Odds</p>
            <div className="mt-2 grid grid-cols-2 gap-3">
              {['Body', 'Goal'].map((label, index) => (
                <div key={label} className="flex items-center justify-between rounded-xl bg-customFontGold/80 px-4 py-2 text-[13px] font-medium text-customBlack shadow-sm">
                  <span>{label}</span>
                  <span>{event.myanOdds[index]}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="grid gap-4 md:grid-cols-2">
            <div>
              <p className="text-xs font-bold text-customBlack/70 dark:text-white/70 italic">Body Live Odds</p>
              <div className="mt-2 grid grid-cols-2 gap-3">
                {['home', 'away', 'handicap'].map((label, index) => {
                  const isHighlighted = (label === 'home' && event.isHomeOdds) || (label === 'away' && !event.isHomeOdds)

                  return (
                    <div
                      key={index}
                      className={`flex items-center justify-between rounded-xl px-2 py-2 text-[13px] font-medium shadow-sm transition-colors
                        ${isHighlighted
                          ? 'bg-customFontRed/10 text-customFontRed'
                          : 'bg-customBlack/5 text-customBlack/70 dark:bg-white/5 dark:text-white/70'
                        }
                      ${label === 'handicap' && 'col-span-2'}
                      `}
                    >
                      <span className='capitalize mr-1'>{label}</span>
                      <span>
                        {
                          label === 'handicap' ? event.odds["homeHandicap"].replace(/\/\+\/-|\/|\+|-/g, "") :
                            event.odds[label].toFixed(2)
                        }
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>

            <div>
              <p className="text-xs font-bold text-customBlack/70 dark:text-white/70 italic">Goal Live Odds</p>
              <div className="mt-2 grid grid-cols-2 gap-3">
                {['over', 'under', 'handicap'].map((label, index) => (
                  <div
                    key={index}
                    className={`flex items-center justify-between rounded-xl px-2 py-2 text-[13px] font-medium shadow-sm transition-color
                      ${label === 'over'
                        ? 'bg-customFontRed/10 text-customFontRed'
                        : 'bg-customBlack/5 text-customBlack/70 dark:bg-white/5 dark:text-white/70'
                      }
                        ${label === 'handicap' && 'col-span-2'}
                      `}
                  >
                    <span className='capitalize mr-1'>{label}</span>
                    <span>
                      {
                        label === 'handicap' ? event.goalOdds[label] :
                        event.goalOdds[label].toFixed(2)
                      }
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section>
            <div className='flex justify-between items-center'>
              <p className="mb-6 text-xs font-bold text-customBlack/70 dark:text-white/70 italic">Exposure</p>
              <div className='flex gap-2 items-center italic text-xs text-customBlack/70 dark:text-white/70 cursor-pointer'>
                <span onClick={() => setExpoTab("body")} className={`${expoTab === "body" ? 'underline font-semibold text-background dark:text-customAccent' : null}`}>Body</span>
                <span>/</span>
                <span onClick={() => setExpoTab("goal")} className={`${expoTab === "goal" ? 'underline font-semibold text-background dark:text-customAccent' : null}`}>Goal</span>
              </div>
            </div>
            <ExposureBar value={ expoTab === "body" ? event.exposures.diffAmountBody : event.exposures.diffAmountGoal } max={event.exposures.limitAmount} />
          </section>

          {/* {
            event?.status === "Upcoming" && (event?.isSingle || event?.isMix) ?
              <section>
                <p className="mb-2 text-sm font-semibold text-customBlack/70 dark:text-white/70 italic">Action Group</p>
                <ActionButtonGroup
                  eventId={event?.eventId}
                  IsFivePercentCom={event?.isFivePercentCom}
                  isSingle={event?.isSingle}
                  isMix={event?.isMix}
                />
                <MyModal />
              </section> : null
          } */}

           {
              <section>
                <p className="mb-2 text-xs font-bold text-customBlack/70 dark:text-white/70 italic">Action Group</p>
                <ActionButtonGroup
                  eventId={event?.eventId}
                  IsFivePercentCom={event?.isFivePercentCom}
                  isSingle={event?.isSingle}
                  isMix={event?.isMix}
                  event={event}
                  userDetails={userDetails}
                  setEventList={setEventList}
                />
                {/* <MyModal /> */}
              </section>
          }
        </div>
      )}

      {activeTab === 'Details' && (
        <div className="mt-4 space-y-3">
          <div className="rounded-md bg-white/80 p-2 shadow-lg dark:bg-white/5 border border-customBlack/10 dark:border-custom_dark_secondary">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className='flex gap-2 items-center'>
                <p className="text-sm font-semibold text-customBlack dark:text-white">Voucher Details</p>
                <input
                  type='text'
                  placeholder="Search vouchers..."
                  value={voucherSearchQuery}
                  onChange={(event) => setVoucherSearchQuery(event.target.value)}
                  className="rounded-md border p-[5px] border-gray-400 bg-white text-sm text-customPrimaryText outline-none transition focus:border-customAccentBlue focus:ring-2 focus:ring-customAccentBlue/40 dark:bg-customSurface dark:text-customHeading"
                /> 
              </div>
                <p className="text-xs text-customBlack/60 dark:text-white/50">Review the latest betting vouchers tied to this event.</p> 
              {/* <StatusBadge value={event.status} /> */}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 rounded-md border border-dashed border-customBlack/20 bg-white/70 p-2 text-center text-sm text-customBlack/60 dark:border-white/10 dark:bg-white/5 dark:text-white/60">
              <MdDataSaverOff className="h-6 w-6 animate-spin-fast"/>
              <span>Loading voucher details...</span>
            </div>
          ) : error ? (
            <div className="rounded-md border border-customFontRed/30 bg-customFontRed/10 p-6 text-center text-sm font-medium text-customFontRed dark:border-customFontRed/40 dark:bg-customFontRed/10">
              {error}
            </div>
          ) : hasVouchers ? (
            <>
              <div className="overflow-hidden rounded-md border border-customBlack/10 bg-white/70 shadow-sm dark:border-white/10 dark:bg-white/5">
                <div className="hidden md:block">
                  <table className="w-full table-fixed">
                    <thead className="border-b border-customBlack/10 text-xs font-semibold uppercase tracking-wide text-customBlack/80 dark:border-white/10 dark:text-white/50">
                      <tr>
                        <th scope="col" className="px-3 py-3 text-center w-[150px]">Voucher</th>
                        {/* <th scope="col" className="px-5 py-3 text-right">Amount</th> */}
                        <th scope="col" className="px-3 py-3 text-center w-[50px]">Status</th>
                        <th scope="col" className="px-3 py-3 text-center w-[50px]">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-customBlack/10 dark:divide-white/10">
                      {paginatedVouchers.map(voucher => {
                        const displayStatus = voucher.active ? 'Active' : 'Settled'
                        const statusStyle = voucherStatusStyles[displayStatus] || 'bg-slate-500/15 text-slate-300'
                        const amount = currencyFormatter.format(voucher.amount)
                        const voucherId = voucher.voucherNo ?? voucher.id ?? '-'
                        const bettingType = voucher.gamblingType ?? voucher.type ?? '-'
                        const username = voucher.user ?? voucher.userName ?? '-'

                        console.log("Rendering voucher:", { voucherId, amount, displayStatus, bettingType, username })

                        return (
                          <tr key={voucher.id ?? voucher.voucherNo} className="text-sm text-customBlack/80 dark:text-white/80">
                            <td className="px-3 py-3 text-left">
                              <div className="flex flex-col">
                                <span className="text-xs text-customBlack dark:text-white">{voucherId}</span>
                                <div className="flex gap-2 items-center">
                                  <span className="truncate text-xs text-customMustardYellow">{bettingType === 'Mix' ? 'Parlay' : bettingType}</span>
                                  <span className="text-xs font-semibold">{amount}</span>
                                  <span className='text-xs font-semibold text-customAccent'>{username}</span>
                                </div>
                              </div>
                            </td>
                            {/* <td className="px-5 py-3 text-right">
                              <div className='flex flex-col'>
                                <span className="text-xs font-semibold">{amount}</span>
                                <span className='text-xs font-semibold text-customAccent'>{username}</span>
                              </div>
                            </td> */}
                            <td className="px-3 py-3 text-right">
                              <span className={`inline-flex items-center rounded-md px-3 py-1 text-xs font-semibold ${statusStyle} italic`}>
                                {displayStatus}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-right">
                              <div className='flex gap-1 items-center flex-row-reverse justify-center'>
                                <PencilSquareIcon className="h-5 w-5 text-customAccent hover:text-customAccentHover" />
                                <CurrencyDollarIcon className="h-5 w-5 text-customAccent hover:text-customAccentHover" />
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="md:hidden divide-y divide-customBlack/10 dark:divide-white/10">
                  {paginatedVouchers.map(voucher => {
                    const displayStatus = voucher.active ? 'Active' : 'Settled'
                    const statusStyle = voucherStatusStyles[displayStatus] || 'bg-slate-500/15 text-slate-300'
                    const amount = currencyFormatter.format(voucher.amount)
                    const voucherId = voucher.voucherNo ?? voucher.id ?? '-'
                    const userName = voucher.user ?? voucher.userName ?? '-'
                    const bettingType = voucher.gamblingType ?? voucher.type ?? '-'

                    return (
                      <div key={voucher.id ?? voucher.voucherNo} className="space-y-2 px-3 py-3">
                        <div className="flex items-center justify-between">
                          <p className="text-base font-semibold text-customBlack dark:text-white">{bettingType}</p>
                          <span className={`inline-flex items-center rounded-md px-3 py-1 text-xs font-semibold ${statusStyle} italic`}>
                            {displayStatus}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-xs text-customBlack/70 dark:text-white/70">
                          <div>
                            <p className="font-medium uppercase tracking-wide">User</p>
                            <p className="text-sm text-customBlack dark:text-white/80">{userName}</p>
                          </div>
                          <div>
                            <p className="font-medium uppercase tracking-wide">Amount</p>
                            <p className="text-sm text-customBlack dark:text-white/80">{amount}</p>
                          </div>
                          <div>
                            <p className="font-medium uppercase tracking-wide">Betting Type</p>
                            <p className="text-sm text-customBlack dark:text-white/80">{bettingType === 'Mix' ? 'Parlay' : bettingType}</p>
                          </div>
                          <div>
                            <p className="font-medium uppercase tracking-wide">Voucher ID</p>
                            <p className="text-sm text-customBlack dark:text-white/80">{voucherId}</p>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
              <Pagination
                page={voucherSafePage}
                pageSize={voucherPageSize}
                totalItems={filteredVouchers.length}
                onPageChange={handleVoucherPageChange}
                onPageSizeChange={handleVoucherPageSizeChange}
                pageSizeOptions={[5]}
                showRowsPerPageLabel={false}
                className="mt-3"
              />
            </>
          ) : (
            <div className="rounded-2xl border border-dashed border-customBlack/20 bg-white/70 p-6 text-center text-sm text-customBlack/60 dark:border-white/10 dark:bg-white/5 dark:text-white/50">
              {noVoucherMessage}
            </div>
          )}
        </div>
      )}
    </aside>
  )
}

export default SelectedEventCard
